import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Menu, X, UserRound, LogIn, UserPlus, KeyRound, LayoutDashboard, LogOut } from "lucide-react";
import { NAV_ITEMS } from "../../lib/data";
import { useAuth } from "../../context/AuthContext";
import { useCompany } from "../../context/CompanyContext";

export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -20 });
  else el.scrollIntoView({ behavior: "smooth" });
};

// Scroll-spy: returns the id of the section currently in view.
const useActiveSection = () => {
  const [active, setActive] = useState("home");
  useEffect(() => {
    // Only observe items that are page anchors (no `path` route override).
    const ids = NAV_ITEMS.filter((n) => !n.path).map((n) => n.id);
    const els = ids.map((id) => document.getElementById(id)).filter(Boolean);
    if (!els.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-45% 0px -45% 0px", threshold: [0, 0.25, 0.5, 1] }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
  return active;
};

const NavLinks = ({ onNavigate, prefix = "nav", activeId }) =>
  NAV_ITEMS.map((item) => {
    const isActive = activeId === item.id;
    return (
      <button
        key={item.id}
        data-testid={`${prefix}-${item.id}`}
        onClick={() => onNavigate(item)}
        aria-current={isActive ? "true" : undefined}
        className={`font-body uppercase tracking-[0.12em] text-[13px] sm:text-sm transition-colors duration-300 ${
          isActive ? "text-[#d6d400]" : "text-[#c9c9ba] hover:text-[#d6d400]"
        }`}
      >
        {item.label}
      </button>
    );
  });

// Dynamic Account dropdown. Signed-out users see Login / Register / Forgot.
// Signed-in users see Dashboard / Profile / Reservations / Logout.
const AccountMenu = ({ variant = "desktop", testIdPrefix = "sticky" }) => {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const go = (path) => { setOpen(false); navigate(path); };
  const doLogout = async () => { setOpen(false); try { await logout(); } catch {} navigate("/"); };

  const signedIn = user && user !== false;
  const items = signedIn
    ? [
        { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, onClick: () => go("/account") },
        { key: "trips", label: "Reservations", icon: ArrowRight, onClick: () => go("/account/trips") },
        { key: "profile", label: "Profile", icon: UserRound, onClick: () => go("/account/profile") },
        { key: "logout", label: "Log Out", icon: LogOut, onClick: doLogout, danger: true },
      ]
    : [
        { key: "login", label: "Log In", icon: LogIn, onClick: () => go("/login") },
        { key: "register", label: "Register", icon: UserPlus, onClick: () => go("/register") },
        { key: "forgot", label: "Forgot Password", icon: KeyRound, onClick: () => go("/forgot-password") },
      ];

  const triggerClass = variant === "hero"
    ? "inline-flex items-center gap-1.5 font-body uppercase tracking-[0.12em] text-xs text-[#c9c9ba] hover:text-[#d6d400] transition-colors"
    : "hidden sm:inline-flex items-center gap-2 font-body uppercase tracking-[0.12em] text-xs text-[#c9c9ba] hover:text-[#d6d400] transition-colors";
  const iconSize = variant === "hero" ? 13 : 15;

  return (
    <div ref={wrapRef} className="relative">
      <button
        data-testid={`${testIdPrefix}-account`}
        onClick={() => setOpen((s) => !s)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={triggerClass}
      >
        <UserRound size={iconSize} /> {signedIn ? (user.first_name || "Account") : "Account"}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18 }}
            role="menu"
            data-testid={`${testIdPrefix}-account-menu`}
            className="absolute right-0 top-[calc(100%+10px)] w-56 z-50 rounded-xl border border-[#d6d40033] bg-[#080808] shadow-[0_20px_60px_rgba(0,0,0,0.6)] overflow-hidden"
          >
            {signedIn && (
              <div className="px-4 py-3 border-b border-[#d6d40018]">
                <div className="font-body text-[10px] tracking-[0.24em] uppercase text-[#8f9080]">Signed in</div>
                <div className="font-display font-bold text-[#f4f4ea] text-sm truncate mt-0.5">{user.email}</div>
              </div>
            )}
            <ul className="py-1.5">
              {items.map((it) => {
                const Icon = it.icon;
                return (
                  <li key={it.key}>
                    <button
                      data-testid={`${testIdPrefix}-account-${it.key}`}
                      onClick={it.onClick}
                      role="menuitem"
                      className={`w-full flex items-center gap-3 px-4 py-2.5 font-body text-[13px] transition-colors ${
                        it.danger ? "text-[#ff9a9a] hover:bg-[#ff6b6b12]" : "text-[#c9c9ba] hover:text-[#d6d400] hover:bg-[#d6d40008]"
                      }`}
                    >
                      <Icon size={14} />
                      <span className="tracking-[0.02em]">{it.label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Mobile menu account link stack — inline version of AccountMenu.
const MobileAccountLinks = ({ onClose }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const go = (path) => { onClose(); navigate(path); };
  const doLogout = async () => { onClose(); try { await logout(); } catch {} navigate("/"); };
  const signedIn = user && user !== false;

  const links = signedIn
    ? [
        { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, onClick: () => go("/account") },
        { key: "trips", label: "Reservations", icon: ArrowRight, onClick: () => go("/account/trips") },
        { key: "profile", label: "Profile", icon: UserRound, onClick: () => go("/account/profile") },
        { key: "logout", label: "Log Out", icon: LogOut, onClick: doLogout, danger: true },
      ]
    : [
        { key: "login", label: "Log In", icon: LogIn, onClick: () => go("/login") },
        { key: "register", label: "Register", icon: UserPlus, onClick: () => go("/register") },
        { key: "forgot", label: "Forgot Password", icon: KeyRound, onClick: () => go("/forgot-password") },
      ];

  return (
    <div className="mt-6 pt-5 border-t border-[#d6d40018] flex flex-col gap-1" data-testid="mobile-account-menu">
      <div className="font-body text-[10px] tracking-[0.24em] uppercase text-[#8f9080] mb-1">Account</div>
      {links.map((it) => {
        const Icon = it.icon;
        return (
          <button
            key={it.key}
            data-testid={`mobile-account-${it.key}`}
            onClick={it.onClick}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg font-body text-[13px] uppercase tracking-[0.1em] transition-colors ${
              it.danger ? "text-[#ff9a9a] hover:bg-[#ff6b6b12]" : "text-[#c9c9ba] hover:text-[#d6d400] hover:bg-[#d6d40008]"
            }`}
          >
            <Icon size={14} /> {it.label}
          </button>
        );
      })}
    </div>
  );
};

// Slim sticky bar: on mobile (< lg) it stays visible from the top so the hamburger
// menu is always reachable. On desktop it fades in after scrolling past the hero.
const StickyBar = () => {
  const [show, setShow] = useState(false);
  const [open, setOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 1024 : true
  );
  const activeId = useActiveSection();
  const navigate = useNavigate();
  const { openContactModal } = useCompany();

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 1024);
    const onScroll = () => setShow(window.scrollY > 520);
    window.addEventListener("resize", onResize, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true });
    onResize();
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  // On mobile, always show the sticky bar (so hamburger stays reachable).
  const visible = show || !isDesktop;

  // Lock body scroll + close on Escape when the mobile panel is open.
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    if (open) {
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", onKey);
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const go = (item) => {
    setOpen(false);
    // Items with an explicit `path` are dedicated pages (Services, About,
    // Contact) — everything else is a landing-page anchor.
    if (item && item.path) {
      navigate(item.path);
      return;
    }
    scrollToId(typeof item === "string" ? item : item.id);
  };

  return (
    <>
      <motion.header
        data-testid="sticky-nav"
        initial={{ y: -80, opacity: 0 }}
        animate={visible ? { y: 0, opacity: 1 } : { y: -80, opacity: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="fixed top-0 left-0 right-0 z-50 border-b border-[#d6d40022] bg-[#050505]/92 backdrop-blur-md"
        style={{ pointerEvents: visible ? "auto" : "none" }}
      >
        <nav aria-label="Primary" className="mx-auto max-w-7xl px-4 sm:px-5 h-14 sm:h-16 flex items-center justify-between gap-3">
          <button
            onClick={() => scrollToId("home")}
            className="flex items-center gap-2 sm:gap-3 min-w-0"
            data-testid="sticky-logo"
            aria-label="VOOR BLACK — back to top"
          >
            <img src="/voor-logo.png" alt="VOOR BLACK logo" className="h-8 w-8 sm:h-9 sm:w-9 object-contain shrink-0" style={{ mixBlendMode: "lighten", filter: "brightness(1.4) contrast(1.05)" }} />
            <span className="font-display font-bold tracking-[0.2em] sm:tracking-[0.22em] text-[#d6d400] text-[11px] sm:text-sm whitespace-nowrap">VOOR BLACK</span>
          </button>
          <div className="hidden lg:flex items-center gap-7">
            <NavLinks onNavigate={go} prefix="sticky-nav" activeId={activeId} />
          </div>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <AccountMenu variant="desktop" testIdPrefix="sticky" />
            <button
              data-testid="sticky-get-in-touch"
              onClick={openContactModal}
              className="vb-btn hidden md:inline-flex items-center gap-2 px-4 sm:px-5 py-2 text-[11px] sm:text-xs"
            >
              Get In Touch <ArrowRight size={14} />
            </button>
            <button
              className="lg:hidden text-[#d6d400] p-1.5 sm:p-2"
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-toggle"
              aria-label="Open menu"
              aria-expanded={open}
            >
              <Menu size={22} />
            </button>
          </div>
        </nav>
      </motion.header>

      {/* Mobile slide-out navigation panel */}
      <AnimatePresence>
        {open && (
          <div className="lg:hidden fixed inset-0 z-[60]" role="dialog" aria-modal="true" aria-label="Menu">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="absolute inset-0 bg-black/70 backdrop-blur-sm"
              data-testid="mobile-menu-overlay"
              onClick={() => setOpen(false)}
            />
            <motion.aside
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
              className="absolute top-0 right-0 h-[100dvh] max-h-[100dvh] w-[78%] max-w-xs bg-[#080808] border-l border-[#d6d40033] p-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] flex flex-col overflow-y-auto overscroll-contain touch-pan-y"
              data-testid="mobile-menu-panel"
            >
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-2">
                  <img src="/voor-logo.png" alt="" aria-hidden className="h-8 w-8 object-contain" style={{ mixBlendMode: "lighten", filter: "brightness(1.4) contrast(1.05)" }} />
                  <span className="font-display font-bold tracking-[0.2em] text-[#d6d400] text-sm">VOOR BLACK</span>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="text-[#d6d400] p-1"
                  aria-label="Close menu"
                  data-testid="mobile-menu-close"
                >
                  <X size={24} />
                </button>
              </div>
              <div className="flex flex-col gap-5">
                <NavLinks onNavigate={go} prefix="mobile-nav" activeId={activeId} />
              </div>
              <button
                onClick={() => { setOpen(false); openContactModal(); }}
                className="vb-btn inline-flex items-center justify-center gap-2 px-5 py-3 text-xs mt-8"
                data-testid="mobile-get-in-touch"
              >
                Get In Touch <ArrowRight size={14} />
              </button>
              <MobileAccountLinks onClose={() => setOpen(false)} />
            </motion.aside>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

// Masked line-by-line reveal for hero headline.
const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const navigate = useNavigate();
  const { openContactModal } = useCompany();
  const heroGo = (item) => {
    if (item && item.path) {
      navigate(item.path);
      return;
    }
    scrollToId(typeof item === "string" ? item : item.id);
  };
  return (
    <header id="home" data-testid="hero-section" className="relative pt-16 sm:pt-20 lg:pt-6 pb-2 px-4">
      <StickyBar />

      {/* corner mark — hidden on very small screens; brighter artwork on visible ones */}
      <img
        src="/voor-logo.png"
        alt=""
        aria-hidden
        className="hidden sm:block absolute top-3 left-3 sm:top-4 sm:left-4 h-8 w-8 sm:h-9 sm:w-9 object-contain opacity-90"
        style={{ mixBlendMode: "lighten", filter: "brightness(1.4) contrast(1.05)" }}
      />

      <div className="mx-auto max-w-6xl flex flex-col items-center text-center">
        {/* VOOR [logo] BLACK */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.05 }}
          className="flex items-center justify-center gap-1.5 sm:gap-3 lg:gap-5 mt-1 w-full flex-nowrap"
        >
          <span className="vb-outline vb-hero-word whitespace-nowrap">VOOR</span>
          <img
            data-testid="hero-logo"
            src="/voor-logo.png"
            alt="VOOR BLACK logo"
            width={1254}
            height={1254}
            loading="eager"
            decoding="async"
            fetchPriority="high"
            className="vb-badge shrink-0 -mx-1"
          />
          <span className="vb-outline vb-hero-word whitespace-nowrap">BLACK</span>
        </motion.div>

        {/* TIME IS WEALTH — masked reveal */}
        <div className="overflow-hidden mt-1 sm:mt-2.5">
          <motion.h1
            data-testid="hero-slogan"
            className="vb-fill vb-hero-title"
            variants={line}
            custom={0}
            initial="hidden"
            animate="show"
          >
            Time Is Wealth
          </motion.h1>
        </div>

        {/* secondary slogan */}
        <div className="overflow-hidden mt-1 sm:mt-1.5 w-full">
          <motion.p
            data-testid="hero-subslogan"
            className="vb-hero-sub font-display font-bold uppercase text-[#d6d400] px-2 mx-auto max-w-2xl"
            variants={line}
            custom={1}
            initial="hidden"
            animate="show"
            style={{ textShadow: "0 0 8px rgba(241,240,0,0.28)" }}
          >
            Website Simplified Because We Respect Yours
          </motion.p>
        </div>

        {/* divider */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="vb-divider w-full max-w-4xl mt-3 sm:mt-5"
        >
          <span className="text-[10px] rotate-45 inline-block">◆</span>
        </motion.div>

        {/* Inline hero nav — desktop only. On tablet/mobile the sticky bar hamburger
            is always visible, so we hide this row to prevent multi-row wrapping.
            The redundant in-hero "Get In Touch" action has been removed — the
            action remains available in the sticky header. */}
        <nav
          className="vb-hero-nav mt-3 sm:mt-4 w-full hidden lg:flex flex-nowrap items-center justify-center gap-x-6 lg:gap-x-7"
        >
          <NavLinks onNavigate={heroGo} />
          <AccountMenu variant="hero" testIdPrefix="hero" />
        </nav>
      </div>
    </header>
  );
}
